import { useState } from "react";
import { Mail, MapPin, Globe, Phone, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";

const EMPTY = { name: "", surname: "", email: "", phone: "", question: "" };

export default function Contact() {
  const { t } = useLang();
  const { address_line, email, web_line, phone, map_lat, map_lng } = useSettings();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const delta = 0.015;
  const bbox = `${map_lng - delta}%2C${map_lat - delta}%2C${map_lng + delta}%2C${map_lat + delta}`;
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${map_lat}%2C${map_lng}`;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 fade-in" data-testid="contact-page">
      <p className="tracking-eyebrow text-[var(--eafg-muted)]">{t("contact.subtitle")}</p>
      <h1 className="font-serif-display text-5xl md:text-6xl mt-3 mb-14">{t("contact.title")}</h1>

      <div className="grid md:grid-cols-12 gap-12 md:gap-20">
        {/* Info */}
        <div className="md:col-span-5 space-y-8">
          <div>
            <p className="tracking-eyebrow text-[var(--eafg-muted)] mb-3">
              <MapPin size={14} strokeWidth={1.5} className="inline mr-2" />
              {t("contact.address")}
            </p>
            <p className="font-serif-display text-2xl">{address_line}</p>
          </div>
          <div>
            <p className="tracking-eyebrow text-[var(--eafg-muted)] mb-3">
              <Globe size={14} strokeWidth={1.5} className="inline mr-2" />
              {t("contact.web")}
            </p>
            <p className="font-serif-display text-2xl">{web_line}</p>
          </div>
          <div>
            <p className="tracking-eyebrow text-[var(--eafg-muted)] mb-3">
              <Mail size={14} strokeWidth={1.5} className="inline mr-2" />
              Email
            </p>
            <p className="font-serif-display text-2xl">{email}</p>
          </div>
          {phone && (
            <div>
              <p className="tracking-eyebrow text-[var(--eafg-muted)] mb-3">
                <Phone size={14} strokeWidth={1.5} className="inline mr-2" />
                Teléfono
              </p>
              <p className="font-serif-display text-2xl">{phone}</p>
            </div>
          )}
          <div className="aspect-[4/3] border border-[var(--eafg-line)] overflow-hidden" data-testid="contact-map">
            <iframe key={mapSrc} title="Mapa de ubicación" src={mapSrc} className="w-full h-full" loading="lazy" />
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-7">
          {sent ? (
            <div className="border border-[var(--eafg-copper)] bg-white p-10 flex flex-col items-center justify-center gap-5 text-center min-h-[320px]" data-testid="contact-success">
              <CheckCircle size={48} strokeWidth={1} className="text-[var(--eafg-copper)]" />
              <h2 className="font-serif-display text-3xl">Mensaje enviado</h2>
              <p className="text-[var(--eafg-muted)] max-w-xs leading-relaxed">
                Gracias por ponerte en contacto. Te responderemos lo antes posible.
              </p>
              <button
                onClick={() => setSent(false)}
                className="mt-2 tracking-eyebrow text-sm border-b border-[var(--eafg-ink)] hover:border-[var(--eafg-copper)] hover:text-[var(--eafg-copper)] transition-colors"
              >
                Enviar otro mensaje
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6 border border-[var(--eafg-line)] p-8 bg-white" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("contact.formName")} required value={form.name} onChange={onChange("name")} testId="cf-name" />
                <Field label={t("contact.formSurname")} required value={form.surname} onChange={onChange("surname")} testId="cf-surname" />
              </div>
              <Field label={t("contact.formEmail")} type="email" required value={form.email} onChange={onChange("email")} testId="cf-email" />
              <Field label={t("contact.formPhone")} value={form.phone} onChange={onChange("phone")} testId="cf-phone" />
              <label className="block">
                <span className="tracking-eyebrow text-[var(--eafg-muted)] block mb-2">{t("contact.formQuestion")}</span>
                <textarea
                  required rows={5} value={form.question} onChange={onChange("question")}
                  className="w-full border border-[var(--eafg-line)] bg-transparent p-3 focus:outline-none focus:border-[var(--eafg-ink)]"
                  data-testid="cf-question"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="bg-[var(--eafg-ink)] text-white px-7 py-4 tracking-eyebrow hover:bg-[var(--eafg-copper)] transition-colors disabled:opacity-50 flex items-center gap-3"
                data-testid="contact-submit"
              >
                {submitting && (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {submitting ? "Enviando…" : t("contact.submit")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <label className="block">
      <span className="tracking-eyebrow text-[var(--eafg-muted)] block mb-2">{label}</span>
      <input
        type={type} required={required} value={value} onChange={onChange}
        className="w-full border-b border-[var(--eafg-line)] bg-transparent py-2 focus:outline-none focus:border-[var(--eafg-ink)]"
        data-testid={`field-${testId}`}
      />
    </label>
  );
}
